import React from 'react';
import { CheckCircle, XCircle, Lock } from 'lucide-react';
import {
  parseFilterConditions,
  generateFilterSummary,
  generateActionDescription,
} from '../utils/filterParser';

interface FilterConditionDisplayProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: Record<string, any>;
  isUnlocked: boolean;
  className?: string;
}

/**
 * Component that displays filter conditions in a human-readable format
 * Shows filter logic, conditions, and action with visual indicators
 */
export const FilterConditionDisplay: React.FC<FilterConditionDisplayProps> = ({
  config,
  params,
  isUnlocked,
  className = '',
}) => {
  const parsed = parseFilterConditions(config, params);

  if (!parsed) {
    return null;
  }

  // Freemium: Show basic info for free, full details for premium
  const showFullDetails = isUnlocked;

  // Determine action icon
  const ActionIcon = parsed.defaultAction === 'continue' ? CheckCircle : XCircle;
  const actionColor = parsed.defaultAction === 'continue'
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-600 dark:text-red-400';

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header with condition count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ActionIcon className={`w-5 h-5 ${actionColor}`} />
          <h5 className="text-sm font-semibold">
            Filter Conditions
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
              {parsed.conditionCount} {parsed.conditionCount === 1 ? 'condition' : 'conditions'}
            </span>
          </h5>
        </div>

        {parsed.conditionCount > 1 && (
          <span className="text-xs font-medium px-2 py-1 bg-muted rounded">
            {parsed.logic}
          </span>
        )}
      </div>

      {/* Free preview: summary only */}
      {!showFullDetails && (
        <div className="relative">
          <div className="blur-sm select-none pointer-events-none bg-muted/50 rounded-lg p-3 border border-border">
            <p className="text-sm text-foreground">
              {generateFilterSummary(parsed, false)}
            </p>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background/90 px-3 py-1.5 rounded-full border border-border">
              <Lock className="w-3 h-3" />
              <span>Unlock to see filter logic</span>
            </div>
          </div>
        </div>
      )}

      {/* Premium view: full details */}
      {showFullDetails && (
        <div className="space-y-2">
          {/* Conditions list */}
          {parsed.conditions.map((condition, index) => (
            <div key={index} className="relative">
              <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 border border-amber-200 dark:border-amber-900">
                <div className="flex items-start gap-2">
                  {/* Condition number */}
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-xs font-bold bg-amber-200 dark:bg-amber-900 text-amber-900 dark:text-amber-100 rounded-full">
                    {index + 1}
                  </span>

                  {/* Condition text */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium text-foreground">{condition.fieldDisplay}</span>
                      <span className="mx-1.5 text-muted-foreground">{condition.operatorDisplay}</span>
                      <code className="px-1.5 py-0.5 text-xs bg-background/50 rounded border border-border">
                        {String(condition.value)}
                      </code>
                    </p>

                    {/* Show action if it differs from default or is explicit */}
                    {condition.action && condition.action !== parsed.defaultAction && (
                      <p className="text-xs text-muted-foreground mt-1">
                        → Then {condition.action} workflow
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Logic connector between conditions */}
              {index < parsed.conditions.length - 1 && (
                <div className="flex justify-center py-1">
                  <span className="text-xs font-semibold px-2 py-0.5 bg-muted text-muted-foreground rounded">
                    {parsed.logic}
                  </span>
                </div>
              )}
            </div>
          ))}

          {/* Action description */}
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2">
              <ActionIcon className={`w-4 h-4 ${actionColor}`} />
              <p className="text-sm font-medium text-foreground">
                {generateActionDescription(parsed)}
              </p>
            </div>
          </div>

          {/* Grouping info for complex filters */}
          {parsed.hasGrouping && (
            <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
              <strong>Note:</strong> This filter uses complex grouping logic. Conditions are evaluated in groups.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
